import type { KeyboardEvent, ReactNode } from "react";
import { TemplateCard, tripColorFor, vars } from "@pack-it-up/ui-web";
import type { TemplateSummary } from "@pack-it-up/core";
import { PlusIcon } from "../../app/icons.js";

// Dashed "New Template" card at the end of a template grid.
export const NewTemplateCard = ({ onClick }: { onClick: () => void }): ReactNode => {
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="New template"
      onClick={onClick}
      onKeyDown={onKeyDown}
      style={{
        display: "flex",
        alignItems: "center",
        gap: vars.space.md,
        border: `1px dashed ${vars.color.border}`,
        borderRadius: vars.radius.lg,
        padding: vars.space.md,
        cursor: "pointer",
        opacity: 0.7,
        transition: "opacity 150ms ease, border-color 150ms ease, background-color 150ms ease",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.opacity = "1";
        (e.currentTarget as HTMLDivElement).style.borderColor = vars.color.accent;
        (e.currentTarget as HTMLDivElement).style.backgroundColor = vars.color.accentSubtle;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.opacity = "0.7";
        (e.currentTarget as HTMLDivElement).style.borderColor = vars.color.border;
        (e.currentTarget as HTMLDivElement).style.backgroundColor = "transparent";
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "38px",
          height: "38px",
          borderRadius: vars.radius.md,
          backgroundColor: vars.color.surfaceRaised,
          border: `1px solid ${vars.color.border}`,
          flexShrink: 0,
        }}
        aria-hidden
      >
        {PlusIcon(16)}
      </span>
      <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: vars.space.xxs }}>
        <span style={{ fontSize: vars.font.sizeSm, fontWeight: vars.font.weightMedium, color: vars.color.text }}>
          New template
        </span>
        <span style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted }}>
          Build your own from scratch
        </span>
      </span>
    </div>
  );
};

// The template grid — cards + an optional dashed new-template card at the end.
// Shared by Home and My Templates.
export const TemplateGrid = ({
  templates,
  onOpen,
  onNew,
  renderAction,
}: {
  templates: TemplateSummary[];
  onOpen: (id: string) => void;
  onNew?: () => void;
  /** Trailing per-card control, e.g. the "Duplicate" overflow menu (issue #8). */
  renderAction?: (template: TemplateSummary) => ReactNode;
}): ReactNode => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
      gap: vars.space.md,
      marginBottom: vars.space.xl,
    }}
  >
    {templates.map((t) => (
      <TemplateCard
        key={t.id}
        name={t.name}
        color={t.color ?? tripColorFor(t.id)}
        {...(t.emoji !== undefined ? { emoji: t.emoji } : {})}
        onClick={() => onOpen(t.id)}
        {...(renderAction !== undefined ? { action: renderAction(t) } : {})}
      />
    ))}
    {onNew !== undefined && <NewTemplateCard onClick={onNew} />}
  </div>
);
